import {Component, OnInit} from "@angular/core";
import {MeasurementType} from "../../measurement-models";
import {EFormService} from "../e-form.service";
import {EFormIdProvider} from "../e-form-id-provider.service";
import {ActivatedRoute} from "@angular/router";
import {UploadFormConfig} from "../../upload/upload-form-config";
import {emptyUploadFormModel, UploadFormModel} from "../../upload/upload-form-model";
import {PropertyChangeListener} from "../../../tools/form/form-field-config";
import {mapToPatientData} from "../../upload/upload-data-mapper";
import {EForm, EFormData} from "../e-form-model";
import {Notifications, NotificationType} from "../../../tools/notifications/notifications";
import {localizationKey} from "../../../i18n/i18n-model";
import {debounceTime} from "rxjs/operators";
import {Subject} from "rxjs";
import {addEmptyEcgSymptoms} from "../../utils";
import {environment} from "../../../../environments/environment";
import {isMailingInfoSet} from "./is-mailing-info-set";
import {HomeMeasurementStatus} from "../../list/found-measurement";
import {EFormValidator} from "./e-form-validator";
import {measurementTypeSelectionOptions} from "../../measurement-type-select-options";

export const eFormConfiguration = {
  debounceTimeMillis: 1000
};

@Component({
  selector: "eform-form",
  templateUrl: "./e-form-form.component.html",
  standalone: false
})
export class EFormFormComponent implements OnInit, PropertyChangeListener<UploadFormModel> {

  type?: MeasurementType;
  formConfig?: UploadFormConfig;

  isHomeMeasurement?: boolean;
  homeStatus: HomeMeasurementStatus | undefined = undefined;

  mailingInfoSet = false;
  private readonly saveForm = new Subject();

  readonly uploadFormModel: UploadFormModel = emptyUploadFormModel();
  eFormId?: string;

  eFormLoaded = false;
  basicPatientData = false;

  homeEFormValidator: EFormValidator | undefined;

  constructor(
    private readonly eFormService: EFormService,
    private readonly eFormCodeProvider: EFormIdProvider,
    private readonly route: ActivatedRoute,
    private readonly notifications: Notifications
  ) {
  }


  ngOnInit(): void {
    this.eFormId = this.eFormCodeProvider.getEFormId(this.route);
    this.eFormService.getEForm(this.eFormId!)
      .subscribe((eform) => {
        environment.language = eform.language;
        this.type = eform.type;
        this.uploadFormModel.measurementType = eform.type;

        this.isHomeMeasurement = eform.homeStatus !== undefined;
        this.basicPatientData = eform.basicPatientData;
        this.homeStatus = eform.homeStatus;

        this.mailingInfoSet = isMailingInfoSet(eform.homeStatus);
        const data = eform.data;
        data.patientData = addEmptyEcgSymptoms(eform.data.patientData);
        this.mapEFormDataToUploadFormModel(data);
        this.createFormConfig(eform);
        this.eFormLoaded = true;
      }, () => {
        this.notifications.addNotification(NotificationType.ERROR, localizationKey("eFormGetFormError"));
      });

    this
      .saveForm
      .pipe(debounceTime(eFormConfiguration.debounceTimeMillis))
      .subscribe(_ => {
        this.eFormService
          .updateEForm(this.eFormId!, this.mapUploadFormModelToEFormData())
          .subscribe(() => {
            },
            () => {
              this.notifications.addNotification(NotificationType.WARNING, localizationKey("eFormUpdateFormError"));
            }
          );
      });
  }

  private createFormConfig(eForm: EForm) {
    this.homeEFormValidator = this.isHomeMeasurement
      ? new EFormValidator(this.uploadFormModel, {ecgTechnicianAnalysisOnlyEnabled: this.basicPatientData})
      : undefined;

    this.formConfig = new UploadFormConfig(
      {
        model: this.uploadFormModel,
        propertyChangeListener: this,
        validationProvider: (property) => this.homeEFormValidator?.isPropertyValid(property),
        uploadingInitiatedMeasurement: false,
        uploadingOrderedMeasurement: false,
        allowedTypeOptions: measurementTypeSelectionOptions,
        editingMeasurement: false,
        isHomeEForm: this.isHomeMeasurement,
        usesDateOfBirth: eForm.usesDateOfBirth,
        isEForm: true
      }
    );
  }

  handlePropertyChange(property: keyof UploadFormModel, newValue: any): void {

    (this.uploadFormModel as any)[property] = newValue;

    this.homeEFormValidator?.updatePropertyValidation(property);

    this.saveForm.next({});
  }

  private mapEFormDataToUploadFormModel(data: EFormData): void {
    Object.keys(data.patientData).forEach(property => {
      // @ts-ignore
      this.uploadFormModel[property] = data.patientData[property];
    });
    this.uploadFormModel.measurementStartTime = data.measurementStartTime;
  }

  private mapUploadFormModelToEFormData(): EFormData {
    return {
      patientData: mapToPatientData(this.uploadFormModel, "REMOVE_EMPTY_SYMPTOMS"),
      measurementStartTime: this.uploadFormModel.measurementStartTime
    };
  }
}


